import React, { useState, useEffect } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Printer,
  X,
  Trash2,
  CreditCard,
  History,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import InvoicePrint from "@/components/InvoicePrint";
import ChangePaymentModeDialog from "@/components/ChangePaymentModeDialog";
import { formatDateTime } from "@/lib/utils_pharma";

export default function InvoicePreviewDialog({
  open,
  onOpenChange,
  bill,
  shop,
  onDeleteBill,
  onPaymentUpdated,
}) {
  const paperWidth = ["58mm", "72mm", "80mm"].includes(shop?.thermal_paper_width)
    ? shop.thermal_paper_width
    : "72mm";
  const [currentBill, setCurrentBill] = useState(bill);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [audits, setAudits] = useState([]);
  const [showAudits, setShowAudits] = useState(true);

  useEffect(() => {
    setCurrentBill(bill);
    setShowAudits(true);
    if (open && bill?.id) {
      api
        .get(`/bills/${bill.id}/payment-audits`)
        .then((res) => setAudits(res.data || []))
        .catch(() => setAudits([]));
    } else {
      setAudits([]);
    }
  }, [open, bill]);

  const doPrint = () => {
    window.print();
  };

  const handlePaymentSuccess = (updatedBill, audit) => {
    setCurrentBill(updatedBill);
    if (audit) {
      setAudits((prev) => [audit, ...prev]);
    }
    if (onPaymentUpdated) {
      onPaymentUpdated(updatedBill, audit);
    }
  };

  const isCredit = currentBill?.payment_mode === "Credit";

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="sm:max-w-lg md:max-w-xl max-h-[90vh] flex flex-col p-0 overflow-hidden receipt-print-dialog"
          data-testid="invoice-preview-dialog"
        >
          {/* 1. FIXED HEADER */}
          <DialogHeader className="px-5 py-3.5 border-b border-border shrink-0 bg-white no-print">
            <DialogTitle className="flex items-center justify-between text-base">
              <span>Invoice Preview · {paperWidth}</span>
              {currentBill?.bill_no && (
                <span className="font-mono text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                  {currentBill.bill_no}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {/* 2. SCROLLABLE CONTENT AREA */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Centered Receipt Preview Shell */}
            <div className="bg-muted/40 rounded-lg p-4 border border-border/60 flex justify-center receipt-preview-shell">
              <InvoicePrint bill={currentBill} shop={shop} preview />
            </div>

            <div className="text-[11px] text-muted-foreground text-center no-print">
              This is exactly how it will look on your {paperWidth} thermal printer.
            </div>

            {/* Payment Change Audit History Section */}
            {audits.length > 0 && (
              <div className="no-print border border-border rounded-lg bg-card overflow-hidden shadow-sm">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-3.5 py-2.5 bg-muted/30 hover:bg-muted/50 transition-colors text-xs font-semibold text-foreground border-b border-border"
                  onClick={() => setShowAudits(!showAudits)}
                >
                  <span className="flex items-center gap-2">
                    <History size={14} className="text-primary" />
                    <span>Payment Changes History</span>
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                      {audits.length}
                    </Badge>
                  </span>
                  {showAudits ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {showAudits && (
                  <div className="divide-y divide-border/60 p-1">
                    {audits.map((a) => (
                      <div key={a.id} className="p-2.5 text-xs space-y-1">
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {formatDateTime(a.changed_at)}
                          </span>
                          <span>by {a.changed_by}</span>
                        </div>

                        <div className="flex items-center gap-1.5 font-medium">
                          <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                            {a.previous_payment_mode}
                          </Badge>
                          <span className="text-muted-foreground text-xs">→</span>
                          <Badge variant="default" className="text-[10px] py-0 px-1.5 bg-primary">
                            {a.new_payment_mode}
                          </Badge>
                        </div>

                        {a.reason && (
                          <div className="text-[11px] text-muted-foreground bg-muted/40 p-1.5 rounded mt-1">
                            <span className="font-semibold text-foreground">Reason: </span>
                            {a.reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 3. FIXED / STABLE FOOTER ACTIONS */}
          <DialogFooter className="px-5 py-3 border-t border-border bg-muted/20 shrink-0 no-print flex flex-col sm:flex-row sm:justify-between items-stretch sm:items-center gap-2">
            <div className="flex items-center gap-2">
              {onDeleteBill && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDeleteBill(currentBill)}
                  data-testid="preview-delete-bill"
                  className="gap-1.5 h-8 text-xs"
                >
                  <Trash2 size={13} /> Delete
                </Button>
              )}

              {!isCredit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPaymentModalOpen(true)}
                  data-testid="preview-change-payment"
                  className="gap-1.5 h-8 text-xs"
                  title="Correct payment mode"
                >
                  <CreditCard size={13} /> Change Payment
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                data-testid="preview-close"
                className="h-8 text-xs"
              >
                <X size={13} className="mr-1" /> Close
              </Button>
              <Button
                size="sm"
                onClick={doPrint}
                data-testid="preview-print"
                className="gap-1.5 h-8 text-xs"
              >
                <Printer size={13} /> Print Bill
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ChangePaymentModeDialog
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        bill={currentBill}
        onSuccess={handlePaymentSuccess}
      />
    </>
  );
}
